import { useEffect, useState } from "react";
import { Dialog } from "@base-ui-components/react/dialog";
import { MODALS, prettyHost, type ModalKey } from "./modalConfig";

/**
 * One shared app-preview Dialog (Base UI → focus trap, Esc, backdrop close,
 * a11y). Phone variant is framed like a little screen (~390×844); desktop is a
 * wide window (~1200×820). The iframe is only mounted while open, and swaps to
 * about:blank on close so the sandbox worker stops running.
 */
export function AppPreviewModal({
  current,
  onClose,
}: {
  current: ModalKey | null;
  onClose: () => void;
}) {
  const cfg = current ? MODALS[current] : null;
  const [loaded, setLoaded] = useState(false);

  // Reset the loading state each time a new app is opened.
  useEffect(() => {
    setLoaded(false);
  }, [current]);

  const isPhone = cfg?.type === "phone";

  return (
    <Dialog.Root open={!!current} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[1000] bg-[rgba(6,8,12,0.72)] backdrop-blur-sm transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Dialog.Popup
          className={[
            "panel fixed left-1/2 top-1/2 z-[1001] flex -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden",
            "max-h-[100dvh] max-w-[100vw] shadow-[0_24px_80px_rgba(0,0,0,0.55)]",
            "transition-[opacity,transform] duration-150 data-[ending-style]:scale-[.99] data-[ending-style]:opacity-0 data-[starting-style]:scale-[.99] data-[starting-style]:opacity-0",
            isPhone
              ? "h-[min(844px,92dvh)] w-[390px] max-[460px]:h-[100dvh] max-[460px]:w-full rounded-[26px] max-[460px]:rounded-none"
              : "h-[min(820px,90dvh)] w-[min(1200px,94vw)] max-[460px]:h-[100dvh] max-[460px]:w-full rounded-panel max-[460px]:rounded-none",
          ].join(" ")}
        >
          {/* Thin amber top hairline (DESIGN.md modal chrome) */}
          <div className="h-px w-full flex-none bg-amber/70" />
          <div className="flex flex-none items-center gap-2 border-b border-line bg-bg-inset px-3.5 py-2.5">
            <span className="h-2 w-2 flex-none rounded-full bg-amber shadow-glow" />
            <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-text-dim">
              {cfg ? prettyHost(cfg.url) : ""}
            </span>
            <span className="etch-label !text-[9px] flex-none rounded border border-line-2 px-1.5 py-0.5 !text-amber">
              demo
            </span>
            <Dialog.Close
              aria-label="Close preview"
              className="flex-none rounded px-1.5 text-lg leading-none text-text-dim transition-colors hover:text-amber"
            >
              ✕
            </Dialog.Close>
          </div>
          <div className="relative flex-1 bg-bg-inset">
            {!loaded && (
              <div className="etch-label absolute inset-0 flex items-center justify-center !text-text-mute">
                loading…
              </div>
            )}
            {cfg && (
              <iframe
                key={current}
                title={`${cfg.label} preview`}
                src={cfg.url}
                referrerPolicy="no-referrer"
                allow="clipboard-write"
                onLoad={() => setLoaded(true)}
                className="block h-full w-full border-0 bg-white"
              />
            )}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
